// Type-only: the schema itself is never needed at runtime here, and importing
// it as a value would drag `@ever-tutor/et-ui/schemas` — a subpath the released
// build of that package does not currently export — into this module's import
// graph, which is what keeps the util unit-testable.
import type { GestureAction } from "../../shared/schema/AgentActionSchemas";
import type { BoxModel } from "tldraw";
import { Streaming } from "../../shared/types/Streaming";
import { AgentActionUtil, registerActionUtil } from "./AgentActionUtil";

/**
 * A TRANSIENT pointer mark — "the tutor gestures at this", not "the tutor marks
 * this up". THE reference comment for the feature; everything else points here.
 *
 * Unlike every other drawing action this creates NOTHING in the tldraw store.
 * It hands a box to `agent.gestures`, which renders it in tldraw's `Overlays`
 * slot for about a second and a half and then drops it. Overlay-not-shape is
 * the load-bearing decision: `BlurryShapesPartUtil` digests
 * `getCurrentPageShapesSorted()`, `ScreenshotPartUtil` rasterises the shape
 * layer, and undo/redo, sync and persistence all key on store records — so a
 * gesture is structurally invisible to all of them, and its disappearance can
 * never be read back as a student deletion.
 *
 * TIMING needs nothing here. The gateway holds the op until TTS reports the
 * onset of the word its `<op>` tag sat on and ships it with a `timestamp`;
 * `WebSocketTransport.stream` releases it at that point on the audio clock. The
 * one requirement is negative: the util must stay OFF that transport's
 * `selfScheduling` list, which is for actions that schedule themselves and
 * would otherwise be delayed twice.
 */
export const GestureActionUtil = registerActionUtil(
  class GestureActionUtil extends AgentActionUtil<GestureAction> {
    static override type = "gesture" as const;

    override getInfo() {
      // Nothing to show: a gesture leaves no trace on the canvas, so a chat
      // history row for it would describe something the student can no longer
      // look at. (It never reaches history anyway — see savesToHistory.)
      return null;
    }

    /**
     * Deliberately synchronous, returning void.
     *
     * `AgentActionManager.act` forwards whatever this returns to `TldrawAgent`,
     * which awaits every action promise before finishing the turn. Returning a
     * promise that resolved when the animation ended would stall the turn
     * behind a purely decorative 1.4s — and stall the NEXT action behind it,
     * which is exactly the audio desync the timing work exists to prevent.
     */
    override applyAction(action: Streaming<GestureAction>): void {
      // `Streaming<T>` makes every field optional, and the box is arithmetic
      // for the geometry — NaN or Infinity would reach the DOM as unparseable
      // path data. A FLAT box is fine and is rejected by neither test: a
      // horizontal line has `h === 0` and still takes an underline, and the
      // circle pads its radii, so a zero-extent target still gets an ellipse.
      const box = { x: action.x, y: action.y, w: action.w, h: action.h };
      if (!Object.values(box).every((n) => Number.isFinite(n))) return;
      if (box.w! < 0 || box.h! < 0) return;

      this.agent.gestures.show({
        kind: action.kind === "underline" ? "underline" : "circle",
        bounds: box as BoxModel,
        // Resolved against the live tldraw theme in GestureMarks, which
        // falls back to DEFAULT_GESTURE_COLOR for anything it cannot name.
        color: action.color,
      });
    }

    /**
     * A gesture is not part of the canvas the next turn reasons about. Putting
     * it in chat history would tell the model there is a circle on screen long
     * after it has gone, and invite it to refer back to one.
     */
    override savesToHistory(): boolean {
      return false;
    }
  },
);
